// Bottom-Sheet.
//
// Gegenüber der früheren Fassung: echter Dialog (role="dialog", aria-modal),
// Fokusfalle, Escape schließt, der Fokus kehrt danach dorthin zurück, wo er
// herkam, der Hintergrund scrollt nicht mit, und man kann das Sheet
// herunterziehen. Der Titel ist ein Textknoten – kein innerHTML mehr.
import { append, el, haptic } from './dom.js';
import { icon } from './icons.js';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface SheetHandle {
  overlay: HTMLElement;
  body: HTMLElement;
  /** Aktionsleiste am unteren Rand (klebt beim Scrollen). */
  actions: HTMLElement;
  /** Knöpfe anhängen; `null` wird übersprungen (bedingte Aktionen). */
  addActions: (...children: (Node | null | false | undefined)[]) => void;
  close: () => void;
  setTitle: (title: string) => void;
  setBusy: (busy: boolean) => void;
}

export interface SheetOptions {
  title?: string;
  onClose?: () => void;
  /** Verhindert Schließen per Escape/Hintergrund (z.B. während eines Uploads). */
  dismissable?: boolean;
}

const openSheets: SheetHandle[] = [];

// Fortlaufend, nie wiederverwendet: zwei gleichzeitig offene Sheets dürfen
// nicht dieselbe Titel-ID tragen, sonst zeigt `aria-labelledby` des zweiten
// auf die Überschrift des ersten.
let sheetSeq = 0;

export function openSheet(
  content: Node | Node[] | null,
  { title = '', onClose, dismissable = true }: SheetOptions = {},
): SheetHandle {
  const previousFocus = document.activeElement as HTMLElement | null;
  const titleId = `sheet-title-${++sheetSeq}`;

  const titleEl = el('h2', { class: 'sheet-title', id: titleId }, title);
  const body = el('div', { class: 'sheet-body' });
  const actions = el('div', { class: 'sheet-actions' });

  if (content) append(body, Array.isArray(content) ? content : [content]);

  const closeBtn = el(
    'button',
    { class: 'icon-btn', type: 'button', 'aria-label': 'Schließen', onclick: () => close() },
    icon('x'),
  );

  const grip = el('div', { class: 'sheet-grip', 'aria-hidden': 'true' });

  const sheet = el(
    'div',
    {
      class: 'sheet',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
    },
    [grip, el('div', { class: 'sheet-head' }, [titleEl, dismissable ? closeBtn : null]), body],
  );

  const overlay = el('div', { class: 'sheet-overlay' }, sheet);
  document.body.appendChild(overlay);
  lockScroll();

  requestAnimationFrame(() => overlay.classList.add('is-open'));

  // Erstes sinnvolles Ziel fokussieren – aber kein Textfeld auf Mobilgeräten,
  // sonst springt sofort die Tastatur hoch und verdeckt das halbe Sheet.
  requestAnimationFrame(() => {
    const focusables = [...sheet.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const target =
      focusables.find((f) => !isTextInput(f) || matchMedia('(min-width: 640px)').matches) ?? sheet;
    if (target === sheet) sheet.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  let closed = false;
  function close(): void {
    if (closed) return;
    closed = true;
    overlay.classList.remove('is-open');
    // Sofort aus Bedienung und Vorlesereihenfolge nehmen. Während der
    // Ausblend-Animation liegt das Overlay sonst noch über allem – ein direkt
    // danach geöffnetes Sheet wäre für 300 ms nicht anklickbar.
    overlay.setAttribute('inert', '');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.pointerEvents = 'none';
    document.removeEventListener('keydown', onKeydown, true);
    const index = openSheets.indexOf(handle);
    if (index >= 0) openSheets.splice(index, 1);
    if (!openSheets.length) unlockScroll();
    window.setTimeout(() => overlay.remove(), 320);
    previousFocus?.focus?.({ preventScroll: true });
    onClose?.();
  }

  function onKeydown(e: KeyboardEvent): void {
    // Nur das oberste Sheet reagiert.
    if (openSheets[openSheets.length - 1] !== handle) return;

    if (e.key === 'Escape' && dismissable) {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;

    const focusables = [...sheet.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (n) => n.offsetParent !== null,
    );
    if (!focusables.length) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('keydown', onKeydown, true);

  if (dismissable) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    enableDragToDismiss(grip, sheet, close);
  }

  const handle: SheetHandle = {
    overlay,
    body,
    actions,
    addActions: (...children) => append(actions, children.filter(Boolean) as Node[]),
    close,
    setTitle: (t: string) => {
      titleEl.textContent = t;
    },
    setBusy: (busy: boolean) => {
      sheet.setAttribute('aria-busy', String(busy));
    },
  };

  // Die Aktionsleiste hängt am Ende des Körpers, bleibt aber klebrig.
  body.appendChild(actions);
  openSheets.push(handle);
  return handle;
}

function isTextInput(node: HTMLElement): boolean {
  return (
    node instanceof HTMLInputElement ||
    node instanceof HTMLTextAreaElement ||
    node.isContentEditable
  );
}

/** Am Griff nach unten ziehen schließt – wie in nativen Apps. */
function enableDragToDismiss(grip: HTMLElement, sheet: HTMLElement, close: () => void): void {
  let startY = 0;
  let currentY = 0;
  let dragging = false;

  grip.addEventListener('pointerdown', (e: PointerEvent) => {
    dragging = true;
    startY = e.clientY;
    currentY = 0;
    grip.setPointerCapture(e.pointerId);
    sheet.style.transition = 'none';
  });

  grip.addEventListener('pointermove', (e: PointerEvent) => {
    if (!dragging) return;
    currentY = Math.max(0, e.clientY - startY);
    sheet.style.translate = `0 ${currentY}px`;
  });

  const end = () => {
    if (!dragging) return;
    dragging = false;
    sheet.style.transition = '';
    sheet.style.translate = '';
    if (currentY > 110) {
      haptic(8);
      close();
    }
  };

  grip.addEventListener('pointerup', end);
  grip.addEventListener('pointercancel', end);
}

let scrollLocks = 0;
let savedScrollY = 0;

function lockScroll(): void {
  if (scrollLocks++ > 0) return;
  savedScrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${savedScrollY}px`;
  document.body.style.width = '100%';
}

function unlockScroll(): void {
  if (--scrollLocks > 0) return;
  scrollLocks = 0;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  // Nur zurückspringen, wenn vorher wirklich gescrollt war.
  if (savedScrollY) window.scrollTo(0, savedScrollY);
}
